import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, ConfigProvider, Form, InputNumber, Modal, Progress, Steps, Tag, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, LoadingOutlined, SendOutlined } from '@ant-design/icons';
import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useTranslation } from 'react-i18next';
import { fetchRtuPingBatchSnapshot, startRtuPingBatch } from '../../services/rtuService';
import {
  RtuPingAttemptDto,
  RtuPingBatchSnapshotDto,
  RtuPingProgressEventDto,
  RtuPingRunResponseDto,
} from '../../types/RtuPing';
import { RTU } from '../../types/RTU';
import { humanizeStatusReason } from '../../utils/statusDisplayConfig';
import { toUiStatus } from '../../utils/statusConfig';
import { dmsAntdTheme } from '../../config/antdTheme';
import { getWebSocketUrl } from '../../config/apiConfig';
import { isKeycloakEnabled } from '../../config/authConfig';
import { useKeycloak } from '../ConditionalAuthProvider';
import authService from '../../services/authService';
import './RtuPingDialog.css';

interface RtuPingDialogProps {
  visible: boolean;
  rtuId: number | string | null;
  rtuIds?: Array<number | string>;
  selectedRtus?: RTU[];
  rtuName?: string | null;
  onCompleted?: () => void;
  onHide: () => void;
}

interface PingFormValues {
  attemptCount: number;
  timeoutMs: number;
  delayMs: number;
}

export type LiveRun = Omit<Partial<RtuPingRunResponseDto>, 'rtuId' | 'attempts'> & {
  id?: number;
  rtuId: number | string;
  rtuName?: string | null;
  attempts: RtuPingAttemptDto[];
  eventType?: string | null;
};

type RunVisualStatus = 'pending' | 'running' | 'online' | 'unstable' | 'offline' | 'invalid';

const DEFAULT_VALUES: PingFormValues = {
  attemptCount: 3,
  timeoutMs: 2000,
  delayMs: 500,
};

const TERMINAL_BATCH_STATUSES = new Set(['COMPLETED', 'FAILED']);
const ACTIVE_RUN_STATUSES = new Set(['RUNNING', 'RUN_STARTED', 'ATTEMPT_COMPLETED']);
const TERMINAL_ITEM_STATUSES = new Set(['ONLINE', 'UNSTABLE', 'OFFLINE', 'INVALID_TARGET', 'FAILED']);

const statusDisplay = {
  pending: { label: 'Pending', className: 'is-pending' },
  running: { label: 'Pinging', className: 'is-running' },
  online: { label: 'Online', className: 'is-online' },
  unstable: { label: 'Unstable', className: 'is-unstable' },
  offline: { label: 'Offline / Not Reachable', className: 'is-offline' },
  invalid: { label: 'Invalid Target', className: 'is-invalid' },
} satisfies Record<RunVisualStatus, { label: string; className: string }>;

const formatTimestamp = (value: string | null | undefined) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const getSafePingStartError = (error: any, fallback: string): string => {
  const backendMessage = String(error?.response?.data?.message ?? '').trim();
  if (backendMessage && !backendMessage.toLowerCase().includes('timeout')) {
    return backendMessage;
  }
  return fallback;
};

const newClientRunId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `ping-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const attemptFromEvent = (event: RtuPingProgressEventDto): RtuPingAttemptDto | null => {
  if (!event.attemptNumber) {
    return null;
  }
  return {
    attemptNumber: event.attemptNumber,
    startedAtUtc: event.occurredAtUtc || '',
    completedAtUtc: event.occurredAtUtc || '',
    result: event.attemptResult || 'UNRESOLVED',
    responseTimeMs: event.responseTimeMs,
    reason: event.reason,
    rawOutput: event.rawOutput ?? null,
  };
};

const getPendingRtuName = (id: number | string, rtu?: RTU, fallbackName?: string | null): string => {
  const name = rtu?.name?.trim() || fallbackName?.trim();
  return name || `RTU ${id}`;
};

const getPendingTargetIp = (rtu?: RTU): string | null => {
  const publicIp = rtu?.publicIPAddress?.trim();
  return publicIp || null;
};

const normalizeStatusValue = (value: unknown): string => String(value ?? '').toUpperCase();

const getProgressCount = (runItem?: Partial<LiveRun>): number =>
  Math.max(runItem?.attempts?.length ?? 0, (runItem?.successCount ?? 0) + (runItem?.failureCount ?? 0));

const getStatusWeight = (runItem?: Partial<LiveRun>): number => {
  const eventStatus = normalizeStatusValue(runItem?.eventType);
  const resultStatus = normalizeStatusValue(runItem?.resultStatus);
  if (runItem?.completedAtUtc || eventStatus === 'RUN_COMPLETED' || TERMINAL_ITEM_STATUSES.has(eventStatus)) {
    return 3;
  }
  if (ACTIVE_RUN_STATUSES.has(eventStatus) || getProgressCount(runItem) > 0) {
    return 2;
  }
  if (eventStatus === 'PENDING' || resultStatus === 'PENDING') {
    return 0;
  }
  return resultStatus ? 1 : 0;
};

const maxDefined = (left: number | undefined, right: number | undefined): number | undefined => {
  if (left === undefined) {
    return right;
  }
  if (right === undefined) {
    return left;
  }
  return Math.max(left, right);
};

const mergeAttempts = (baseAttempts: RtuPingAttemptDto[], updateAttempts: RtuPingAttemptDto[]): RtuPingAttemptDto[] => {
  if (updateAttempts.length === 0) {
    return baseAttempts;
  }
  const byAttempt = new Map<number, RtuPingAttemptDto>();
  baseAttempts.forEach((attempt) => byAttempt.set(attempt.attemptNumber, attempt));
  updateAttempts.forEach((attempt) => {
    const existing = byAttempt.get(attempt.attemptNumber);
    byAttempt.set(attempt.attemptNumber, {
      ...(existing ?? {}),
      ...attempt,
      rawOutput: attempt.rawOutput ?? existing?.rawOutput ?? null,
    });
  });
  const attempts: RtuPingAttemptDto[] = [];
  byAttempt.forEach((attempt) => attempts.push(attempt));
  return attempts.sort((left, right) => left.attemptNumber - right.attemptNumber);
};

const mergeRun = (base: LiveRun | undefined, update: LiveRun): LiveRun => {
  const updateAttempts = update.attempts ?? [];
  const baseAttempts = base?.attempts ?? [];
  const mergedAttempts = mergeAttempts(baseAttempts, updateAttempts);
  const baseWeight = getStatusWeight(base);
  const updateWeight = getStatusWeight(update);
  const useUpdateState = updateWeight >= baseWeight;
  const baseProgress = getProgressCount(base);
  const updateProgress = getProgressCount(update);
  const updateHasAttemptEvidence = updateAttempts.length > 0;
  const updateIsTerminal = Boolean(update.completedAtUtc) || normalizeStatusValue(update.resultStatus) === 'INVALID_TARGET';
  const useUpdateMeasurements =
    updateIsTerminal || updateHasAttemptEvidence || (useUpdateState && updateProgress > baseProgress);

  return {
    ...(base ?? {}),
    ...update,
    rtuId: update.rtuId ?? base?.rtuId ?? update.id ?? '',
    rtuName: update.rtuName ?? base?.rtuName,
    targetIp: update.targetIp ?? base?.targetIp,
    attemptCount: update.attemptCount ?? base?.attemptCount,
    successCount: useUpdateMeasurements ? maxDefined(update.successCount, base?.successCount) : base?.successCount ?? update.successCount,
    failureCount: useUpdateMeasurements ? maxDefined(update.failureCount, base?.failureCount) : base?.failureCount ?? update.failureCount,
    packetLossPercent: useUpdateMeasurements ? update.packetLossPercent ?? base?.packetLossPercent : base?.packetLossPercent,
    minResponseMs: useUpdateMeasurements ? update.minResponseMs ?? base?.minResponseMs : base?.minResponseMs,
    maxResponseMs: useUpdateMeasurements ? update.maxResponseMs ?? base?.maxResponseMs : base?.maxResponseMs,
    avgResponseMs: useUpdateMeasurements ? update.avgResponseMs ?? base?.avgResponseMs : base?.avgResponseMs,
    resultStatus: useUpdateState ? update.resultStatus ?? base?.resultStatus : base?.resultStatus,
    resultReason: useUpdateState ? update.resultReason ?? base?.resultReason : base?.resultReason,
    completedAtUtc: update.completedAtUtc ?? base?.completedAtUtc,
    eventType: useUpdateState ? update.eventType ?? base?.eventType : base?.eventType,
    attempts: mergedAttempts,
  };
};

const getRunKey = (runItem: LiveRun): string => String(runItem.rtuId ?? runItem.id ?? '');

const getRunName = (runItem: LiveRun): string => {
  const name = runItem.rtuName?.trim();
  return name || `RTU ${runItem.rtuId}`;
};

const getSuccessCount = (runItem: LiveRun): number =>
  runItem.successCount ?? runItem.attempts.filter((attempt) => attempt.result === 'SUCCESS').length;

const getFailureCount = (runItem: LiveRun): number =>
  runItem.failureCount ?? runItem.attempts.filter((attempt) => attempt.result === 'FAILURE').length;

const getAttemptCount = (runItem: LiveRun): number =>
  runItem.attemptCount ?? Math.max(DEFAULT_VALUES.attemptCount, runItem.attempts.length);

const getLastAttemptTime = (runItem: LiveRun): string => {
  const latestAttempt = [...runItem.attempts]
    .sort((left, right) => {
      const leftTime = new Date(left.completedAtUtc || left.startedAtUtc || '').getTime();
      const rightTime = new Date(right.completedAtUtc || right.startedAtUtc || '').getTime();
      return (Number.isNaN(rightTime) ? 0 : rightTime) - (Number.isNaN(leftTime) ? 0 : leftTime);
    })[0];

  return formatTimestamp(latestAttempt?.completedAtUtc || latestAttempt?.startedAtUtc);
};

export const ATTEMPT_DETAILS_NOT_RETAINED_LABEL = 'Not retained';
export const ATTEMPT_DETAILS_NOT_RETAINED_MESSAGE = 'Attempt details are no longer retained';

/**
 * Attempt-level detail is kept only for an RTU's latest completed probe (and for batch items while their probe is
 * still that latest one). A finished item without attempts is therefore "not retained", not "waiting".
 */
const attemptDetailsNotRetained = (runItem: LiveRun): boolean =>
  runItem.attempts.length === 0 && isRunComplete(runItem) && !hasInvalidTarget(runItem);

export const renderAttemptProgress = (runItem: LiveRun) => {
  const attempts = [...runItem.attempts].sort((left, right) => left.attemptNumber - right.attemptNumber);
  const missingAttemptLabel = attemptDetailsNotRetained(runItem) ? ATTEMPT_DETAILS_NOT_RETAINED_LABEL : 'Waiting';
  const attemptsByNumber = new Map(attempts.map((attempt) => [attempt.attemptNumber, attempt]));
  const attemptCount = getAttemptCount(runItem);
  const visualStatus = getRunVisualStatus(runItem);
  const activeAttemptNumber = visualStatus === 'running'
    ? Array.from({ length: attemptCount }, (_, index) => index + 1).find((attemptNumber) => !attemptsByNumber.has(attemptNumber))
    : undefined;

  return (
    <div
      className="rtu-ping-modal__attempt-progress"
      style={{ width: `min(100%, ${Math.min(320, Math.max(96, attemptCount * 32))}px)` }}
      aria-label={`${attemptCount} ping attempts`}
      dir="ltr"
    >
      <Steps
        size="small"
        direction="horizontal"
        labelPlacement="vertical"
        responsive={false}
        current={-1}
        items={Array.from({ length: attemptCount }, (_, index) => {
          const attemptNumber = index + 1;
          const attempt = attemptsByNumber.get(attemptNumber);
          const success = attempt?.result === 'SUCCESS';
          const failed = Boolean(attempt) && !success;
          const active = !attempt && activeAttemptNumber === attemptNumber;
          const status: 'finish' | 'error' | 'process' | 'wait' = success
            ? 'finish'
            : failed
              ? 'error'
              : active
                ? 'process'
                : 'wait';
          const stateLabel = success
            ? `Succeeded · ${formatResponseTime(attempt.responseTimeMs)}`
            : failed
              ? `Failed · ${humanizeStatusReason(attempt?.reason)}`
              : active
                ? 'Pinging'
                : missingAttemptLabel;

          return {
            status,
            title: null,
            icon: (
              <Tooltip title={`Attempt ${attemptNumber} · ${stateLabel}${attempt ? ` · ${formatTimestamp(attempt.completedAtUtc || attempt.startedAtUtc)}` : ''}`}>
                <span
                  className={`rtu-ping-modal__attempt-icon is-${status}`}
                  aria-label={`Attempt ${attemptNumber} ${stateLabel}`}
                >
                  {success ? <CheckOutlined /> : failed ? <CloseOutlined /> : active ? <LoadingOutlined spin /> : attemptNumber}
                </span>
              </Tooltip>
            ),
          };
        })}
      />
    </div>
  );
};

const isRunComplete = (runItem: LiveRun): boolean => {
  const eventStatus = normalizeStatusValue(runItem.eventType);
  const resultStatus = normalizeStatusValue(runItem.resultStatus);
  if (eventStatus === 'RUN_COMPLETED' || Boolean(runItem.completedAtUtc) || TERMINAL_ITEM_STATUSES.has(eventStatus)) {
    return true;
  }
  return !ACTIVE_RUN_STATUSES.has(eventStatus) && eventStatus !== 'PENDING' && TERMINAL_ITEM_STATUSES.has(resultStatus);
};

const hasInvalidTarget = (runItem: LiveRun): boolean => {
  const reason = String(runItem.resultReason ?? '').toUpperCase();
  return reason.includes('MISSING') || reason.includes('INVALID') || reason.includes('UNAVAILABLE');
};

const getRunVisualStatus = (runItem: LiveRun): RunVisualStatus => {
  const eventStatus = normalizeStatusValue(runItem.eventType);
  const resultStatus = normalizeStatusValue(runItem.resultStatus);
  if (eventStatus === 'INVALID_TARGET' || resultStatus === 'INVALID_TARGET' || hasInvalidTarget(runItem)) {
    return 'invalid';
  }
  if (!isRunComplete(runItem) && (ACTIVE_RUN_STATUSES.has(eventStatus) || getProgressCount(runItem) > 0 || Boolean(runItem.id))) {
    return 'running';
  }
  if (eventStatus === 'PENDING' || resultStatus === 'PENDING') {
    return 'pending';
  }
  if (isRunComplete(runItem) && runItem.resultStatus) {
    const uiStatus = toUiStatus(runItem.resultStatus);
    if (uiStatus === 'online' || uiStatus === 'unstable') {
      return uiStatus;
    }
    if (uiStatus === 'offline') {
      return 'offline';
    }
  }
  return 'pending';
};

const getRunStatusLabel = (runItem: LiveRun): string => statusDisplay[getRunVisualStatus(runItem)].label;

const formatResponseTime = (value: number | null | undefined): string =>
  value == null ? '-' : `${Number(value).toFixed(0)} ms`;

const formatTerminalMs = (value: number | null | undefined): string =>
  value == null ? '-' : Number(value).toFixed(value >= 10 ? 0 : 1);

const formatPacketLoss = (value: number | null | undefined): string =>
  `${Number(value ?? 0).toFixed(1)}%`;

const formatTerminalPacketLoss = (value: number | null | undefined): string => {
  const loss = Number(value ?? 0);
  if (Number.isInteger(loss)) {
    return `${loss.toFixed(0)}%`;
  }
  return `${loss.toFixed(1)}%`;
};

const attemptTerminalLines = (attempt: RtuPingAttemptDto, target: string): string[] => {
  if (attempt.result === 'SUCCESS') {
    return [`Reply from ${target}: bytes=32 time=${formatTerminalMs(attempt.responseTimeMs)}ms`];
  }

  if (attempt.result === 'FAILURE') {
    return ['Request timed out.'];
  }

  return [`Ping attempt ${attempt.attemptNumber} did not complete.`];
};

const buildTerminalSummary = (runItem: LiveRun): string => {
  const target = runItem.targetIp || 'target unavailable';
  const lines = [
    '',
    `Ping statistics for ${target}:`,
    `    Packets: Sent = ${getAttemptCount(runItem)}, Received = ${getSuccessCount(runItem)}, Lost = ${getFailureCount(runItem)} (${formatTerminalPacketLoss(
      runItem.packetLossPercent
    )} loss),`,
  ];
  if (getSuccessCount(runItem) > 0) {
    lines.push(
      `Approximate round trip times in milli-seconds:`,
      `    Minimum = ${formatTerminalMs(runItem.minResponseMs)}ms, Maximum = ${formatTerminalMs(runItem.maxResponseMs)}ms, Average = ${formatTerminalMs(
        runItem.avgResponseMs
      )}ms`
    );
  }
  return lines.join('\n');
};

export const buildRawOutput = (runItem: LiveRun): string => {
  const target = runItem.targetIp || 'target unavailable';
  const attempts = [...runItem.attempts].sort((left, right) => left.attemptNumber - right.attemptNumber);
  if (attempts.length === 0) {
    if (getRunVisualStatus(runItem) === 'invalid') {
      return `ping: ${humanizeStatusReason(runItem.resultReason) || 'ping target is invalid'}`;
    }
    if (attemptDetailsNotRetained(runItem)) {
      return `PING ${target}\n${ATTEMPT_DETAILS_NOT_RETAINED_MESSAGE}.\n${buildTerminalSummary(runItem)}`;
    }
    return `PING ${target}\nWaiting for ping attempts...`;
  }

  const outputLines = [`Pinging ${target} with 32 bytes of data:`];
  const attemptLines = attempts.flatMap((attempt) => attemptTerminalLines(attempt, target));
  if (attemptLines.length > 0) {
    outputLines.push(...attemptLines);
  } else if (!isRunComplete(runItem)) {
    outputLines.push('Waiting for ping attempts...');
  }

  if (isRunComplete(runItem)) {
    outputLines.push(buildTerminalSummary(runItem));
  }
  return outputLines.join('\n');
};

const countByStatus = (runs: LiveRun[], status: RunVisualStatus): number =>
  runs.filter((runItem) => getRunVisualStatus(runItem) === status).length;

const mergeBatchSnapshot = (
  previous: RtuPingBatchSnapshotDto | null,
  next: RtuPingBatchSnapshotDto
): RtuPingBatchSnapshotDto => {
  if (!previous || previous.batchRunId !== next.batchRunId) {
    return next;
  }
  const previousTerminal = TERMINAL_BATCH_STATUSES.has(previous.status);
  return {
    ...next,
    status: previousTerminal ? previous.status : next.status,
    completedCount: Math.max(previous.completedCount ?? 0, next.completedCount ?? 0),
    completedAtUtc: next.completedAtUtc ?? previous.completedAtUtc,
  };
};

export const RtuPingDialog = ({
  visible,
  rtuId,
  rtuIds = [],
  selectedRtus = [],
  rtuName,
  onCompleted,
  onHide,
}: RtuPingDialogProps) => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const [form] = Form.useForm<PingFormValues>();
  const [running, setRunning] = useState(false);
  const [currentAttemptCount, setCurrentAttemptCount] = useState(DEFAULT_VALUES.attemptCount);
  const [batchSnapshot, setBatchSnapshot] = useState<RtuPingBatchSnapshotDto | null>(null);
  const [currentBatchRunId, setCurrentBatchRunId] = useState<string | null>(null);
  const [selectedRunKey, setSelectedRunKey] = useState<string | null>(null);
  const [liveRuns, setLiveRuns] = useState<Record<string, LiveRun>>({});
  const [error, setError] = useState<string | null>(null);
  const stompClientRef = useRef<Client | null>(null);
  const completedBatchNotificationRef = useRef<string | null>(null);
  const bulkMode = rtuIds.length > 1;
  const rtuIdsKey = rtuIds.map((id) => String(id)).join('|');

  const targetRtuIds = useMemo<Array<number | string>>(() => {
    if (bulkMode) {
      return rtuIds;
    }
    return rtuId === undefined || rtuId === null ? [] : [rtuId];
  }, [bulkMode, rtuId, rtuIds]);

  const firstTargetKey = useMemo(() => {
    const firstTarget = bulkMode ? rtuIdsKey.split('|').filter(Boolean)[0] : rtuId;
    return firstTarget === undefined || firstTarget === null ? null : String(firstTarget);
  }, [bulkMode, rtuId, rtuIdsKey]);

  const selectedRtuById = useMemo(() => {
    const map = new Map<string, RTU>();
    selectedRtus.forEach((rtu) => {
      if (rtu.id !== undefined && rtu.id !== null) {
        map.set(String(rtu.id), rtu);
      }
    });
    return map;
  }, [selectedRtus]);

  const seededRuns = useMemo<LiveRun[]>(
    () =>
      targetRtuIds.map((id) => {
        const rtu = selectedRtuById.get(String(id));
        return {
          rtuId: id,
          rtuName: getPendingRtuName(id, rtu, !bulkMode ? rtuName : null),
          targetIp: getPendingTargetIp(rtu),
          attemptCount: currentAttemptCount,
          successCount: 0,
          failureCount: 0,
          attempts: [],
          resultStatus: 'PENDING',
          resultReason: null,
          eventType: 'PENDING',
        };
      }),
    [bulkMode, currentAttemptCount, rtuName, selectedRtuById, targetRtuIds]
  );

  useEffect(() => {
    if (visible) {
      form.setFieldsValue(DEFAULT_VALUES);
      setCurrentAttemptCount(DEFAULT_VALUES.attemptCount);
      setBatchSnapshot(null);
      setCurrentBatchRunId(null);
      setSelectedRunKey(firstTargetKey);
      setLiveRuns({});
      setError(null);
      setRunning(false);
      completedBatchNotificationRef.current = null;
    }
  }, [firstTargetKey, form, visible]);

  useEffect(() => {
    return () => {
      stompClientRef.current?.deactivate();
      stompClientRef.current = null;
    };
  }, []);

  const disconnectProgressSocket = () => {
    stompClientRef.current?.deactivate();
    stompClientRef.current = null;
  };

  const handleProgressEvent = useCallback((event: RtuPingProgressEventDto) => {
    if (event.eventType === 'BATCH_COMPLETED' || event.eventType === 'BATCH_FAILED') {
      setRunning(false);
      return;
    }
    if (event.rtuId == null && event.runId == null) {
      return;
    }

    const key = String(event.rtuId ?? event.runId ?? 'single');
    setLiveRuns((previous) => {
      const existing = previous[key] ?? {
        rtuId: event.rtuId ?? key,
        rtuSerialNo: event.rtuSerialNo,
        rtuName: event.rtuName,
        attempts: [],
      };
      const attempt = attemptFromEvent(event);
      const attempts = attempt
        ? [...existing.attempts.filter((item) => item.attemptNumber !== attempt.attemptNumber), attempt].sort(
            (left, right) => left.attemptNumber - right.attemptNumber
          )
        : existing.attempts;
      const eventStatus = normalizeStatusValue(event.eventType);
      const eventResultStatus = ACTIVE_RUN_STATUSES.has(eventStatus) ? 'RUNNING' : event.resultStatus;
      const update: LiveRun = {
        id: event.runId ?? existing.id,
        rtuId: event.rtuId ?? existing.rtuId,
        rtuSerialNo: event.rtuSerialNo ?? existing.rtuSerialNo,
        rtuName: event.rtuName ?? existing.rtuName,
        targetIp: event.targetIp ?? existing.targetIp,
        attemptCount: event.totalAttempts ?? existing.attemptCount,
        successCount: event.successCount ?? undefined,
        failureCount: event.failureCount ?? undefined,
        packetLossPercent: event.packetLossPercent ?? undefined,
        avgResponseMs: event.avgResponseMs ?? undefined,
        resultStatus: eventResultStatus ?? undefined,
        resultReason: event.reason ?? undefined,
        completedAtUtc: event.eventType === 'RUN_COMPLETED' ? event.occurredAtUtc : undefined,
        eventType: event.eventType,
        attempts: attempt ? [attempt] : [],
      };

      return {
        ...previous,
        [key]: mergeRun(existing, { ...update, attempts }),
      };
    });
  }, []);

  const connectProgressSocket = useCallback(
    (clientRunId: string): Promise<void> =>
      new Promise((resolve) => {
        disconnectProgressSocket();
        const keycloakMode = isKeycloakEnabled();
        const token = keycloakMode ? keycloak?.token : authService.getValidToken(false);
        const stompClient = new Client({
          webSocketFactory: () => new SockJS(`${getWebSocketUrl()}/ws`),
          connectHeaders: token ? { Authorization: `Bearer ${token}` } : {},
          reconnectDelay: 0,
          connectionTimeout: 5000,
          debug: () => undefined,
          onConnect: () => {
            stompClient.subscribe(`/topic/rtuPingProgress/${clientRunId}`, (message: IMessage) => {
              try {
                handleProgressEvent(JSON.parse(message.body) as RtuPingProgressEventDto);
              } catch (parseError) {
                console.warn('Failed to parse RTU ping progress event', parseError);
              }
            });
            resolve();
          },
          onStompError: () => resolve(),
          onWebSocketClose: () => resolve(),
        });
        stompClientRef.current = stompClient;
        stompClient.activate();
        window.setTimeout(resolve, 1500);
      }),
    [handleProgressEvent, keycloak?.token]
  );

  const applyBatchSnapshot = useCallback((snapshot: RtuPingBatchSnapshotDto) => {
    setBatchSnapshot((previous) => mergeBatchSnapshot(previous, snapshot));
    setLiveRuns((previous) => {
      const next = { ...previous };
      snapshot.items.forEach((item) => {
        const key = String(item.currentRtuId);
        const fromRun = item.run;
        const itemStatus = normalizeStatusValue(item.status);
        const hasAuthoritativeItemState = Boolean(fromRun?.completedAtUtc) || TERMINAL_ITEM_STATUSES.has(itemStatus);
        next[key] = mergeRun(next[key], {
          ...(fromRun ?? {}),
          rtuId: item.currentRtuId,
          rtuSerialNo: item.rtuSerialNo ?? fromRun?.rtuSerialNo,
          rtuName: item.rtuName ?? next[key]?.rtuName,
          targetIp: item.targetIp ?? fromRun?.targetIp,
          attemptCount: item.attemptCount ?? fromRun?.attemptCount,
          successCount: hasAuthoritativeItemState ? item.successCount ?? fromRun?.successCount : fromRun?.successCount,
          failureCount: hasAuthoritativeItemState ? item.failureCount ?? fromRun?.failureCount : fromRun?.failureCount,
          packetLossPercent: hasAuthoritativeItemState ? item.packetLossPercent ?? fromRun?.packetLossPercent : fromRun?.packetLossPercent,
          avgResponseMs: hasAuthoritativeItemState ? item.avgResponseMs ?? fromRun?.avgResponseMs : fromRun?.avgResponseMs,
          resultStatus: hasAuthoritativeItemState ? fromRun?.resultStatus ?? item.status : itemStatus === 'PENDING' ? 'PENDING' : 'RUNNING',
          resultReason: hasAuthoritativeItemState ? item.reason ?? fromRun?.resultReason : fromRun?.resultReason,
          completedAtUtc: fromRun?.completedAtUtc,
          eventType: item.status,
          attempts: fromRun?.attempts ?? next[key]?.attempts ?? [],
        });
      });
      return next;
    });

    if (TERMINAL_BATCH_STATUSES.has(snapshot.status)) {
      setRunning(false);
      disconnectProgressSocket();
      if (completedBatchNotificationRef.current !== snapshot.batchRunId) {
        completedBatchNotificationRef.current = snapshot.batchRunId;
        onCompleted?.();
      }
    }
  }, [onCompleted]);

  useEffect(() => {
    if (!visible || !currentBatchRunId) {
      return undefined;
    }

    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const snapshot = await fetchRtuPingBatchSnapshot(currentBatchRunId);
        if (cancelled) {
          return;
        }
        applyBatchSnapshot(snapshot);
        if (!TERMINAL_BATCH_STATUSES.has(snapshot.status)) {
          timer = window.setTimeout(poll, 2000);
        }
      } catch (_pollError) {
        if (!cancelled) {
          timer = window.setTimeout(poll, 3000);
        }
      }
    };

    timer = window.setTimeout(poll, 1000);
    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [applyBatchSnapshot, currentBatchRunId, visible]);

  const run = async () => {
    if (targetRtuIds.length === 0) {
      return;
    }
    const values = await form.validateFields();
    const clientRunId = newClientRunId();
    setRunning(true);
    setCurrentBatchRunId(clientRunId);
    setCurrentAttemptCount(values.attemptCount);
    setError(null);
    setBatchSnapshot(null);
    setSelectedRunKey(String(targetRtuIds[0]));
    setLiveRuns(
      seededRuns.reduce<Record<string, LiveRun>>((acc, runItem) => {
        acc[getRunKey(runItem)] = {
          ...runItem,
          attemptCount: values.attemptCount,
        };
        return acc;
      }, {})
    );

    try {
      await connectProgressSocket(clientRunId);
      const snapshot = await startRtuPingBatch({
        rtuIds: targetRtuIds,
        attemptCount: values.attemptCount,
        timeoutMs: values.timeoutMs,
        delayBetweenAttemptsMs: values.delayMs,
        clientRunId,
      });
      applyBatchSnapshot(snapshot);
    } catch (requestError: any) {
      const timedOut = requestError?.code === 'ECONNABORTED' || String(requestError?.message ?? '').toLowerCase().includes('timeout');
      if (timedOut) {
        console.warn('RTU ping start request timed out while progress tracking remains active.', requestError);
        return;
      }

      setError(
        getSafePingStartError(
          requestError,
          t('rtuActions.dialogs.pingRequestFailed.message', {
            defaultValue: 'The ping could not be started. Try again.',
          })
        )
      );
      setRunning(false);
    }
  };

  const runs = useMemo<LiveRun[]>(() => {
    const runsByKey = new Map<string, LiveRun>();
    const orderedKeys: string[] = [];
    const putRun = (runItem: LiveRun) => {
      const key = getRunKey(runItem);
      if (!key) {
        return;
      }
      if (!runsByKey.has(key)) {
        orderedKeys.push(key);
      }
      runsByKey.set(key, mergeRun(runsByKey.get(key), runItem));
    };

    seededRuns.forEach(putRun);
    Object.values(liveRuns).forEach(putRun);
    return orderedKeys.map((key) => runsByKey.get(key)).filter((runItem): runItem is LiveRun => Boolean(runItem));
  }, [liveRuns, seededRuns]);

  useEffect(() => {
    if (runs.length === 0) {
      return;
    }
    if (!selectedRunKey || !runs.some((runItem) => getRunKey(runItem) === selectedRunKey)) {
      setSelectedRunKey(getRunKey(runs[0]));
    }
  }, [runs, selectedRunKey]);

  const selectedRun = runs.find((runItem) => getRunKey(runItem) === selectedRunKey) ?? runs[0] ?? null;
  const completedRuns = Math.max(batchSnapshot?.completedCount ?? 0, runs.filter(isRunComplete).length);
  const requestedRuns = Math.max(batchSnapshot?.requestedCount ?? 0, runs.length);
  const progressPercent = Math.round((completedRuns / Math.max(1, requestedRuns)) * 100);
  const onlineCount = countByStatus(runs, 'online');
  const unstableCount = countByStatus(runs, 'unstable');
  const offlineCount = countByStatus(runs, 'offline');
  const invalidCount = countByStatus(runs, 'invalid');
  const runningCount = countByStatus(runs, 'running');
  const pendingCount = countByStatus(runs, 'pending');
  const title = t('rtuActions.dialogs.pingRun.title', {
    defaultValue: bulkMode ? 'Ping selected RTUs' : 'Ping RTU{{name}}',
    name: !bulkMode && rtuName ? ` - ${rtuName}` : '',
  });

  return (
    <ConfigProvider theme={dmsAntdTheme}>
      <Modal
        title={title}
        open={visible}
        onCancel={onHide}
        footer={null}
        width="min(1520px, calc(100vw - 180px))"
        centered={false}
        style={{ top: 72 }}
        forceRender
        destroyOnHidden
        className="rtu-ping-modal"
      >
        <Form
          form={form}
          layout="inline"
          initialValues={DEFAULT_VALUES}
          className="rtu-ping-modal__form"
          onValuesChange={(_changedValues, values: PingFormValues) => {
            if (typeof values.attemptCount === 'number') {
              setCurrentAttemptCount(values.attemptCount);
            }
          }}
        >
          <div className="rtu-ping-modal__field">
            <span className="rtu-ping-modal__field-label">
              <span aria-hidden="true">*</span>
              {t('rtuActions.dialogs.pingRun.attempts', { defaultValue: 'Attempts' })}
            </span>
            <Form.Item name="attemptCount" rules={[{ required: true }]} noStyle>
              <InputNumber min={1} max={10} />
            </Form.Item>
          </div>
          <div className="rtu-ping-modal__field">
            <span className="rtu-ping-modal__field-label">
              <span aria-hidden="true">*</span>
              {t('rtuActions.dialogs.pingRun.timeout', { defaultValue: 'Timeout (ms)' })}
            </span>
            <Form.Item name="timeoutMs" rules={[{ required: true }]} noStyle>
              <InputNumber min={500} max={10000} step={500} />
            </Form.Item>
          </div>
          <div className="rtu-ping-modal__field">
            <span className="rtu-ping-modal__field-label">
              <span aria-hidden="true">*</span>
              {t('rtuActions.dialogs.pingRun.delay', { defaultValue: 'Delay (ms)' })}
            </span>
            <Form.Item name="delayMs" rules={[{ required: true }]} noStyle>
              <InputNumber min={0} max={10000} step={250} />
            </Form.Item>
          </div>
          <Button type="primary" icon={<SendOutlined />} onClick={run} loading={running} disabled={targetRtuIds.length === 0} className="rtu-ping-modal__run">
            {running ? t('common.loading', { defaultValue: 'Running...' }) : t('rtuActions.items.ping', { defaultValue: 'Ping RTU' })}
          </Button>
        </Form>

        {error ? (
          <Alert
            className="rtu-ping-modal__alert"
            type="error"
            message={error}
            showIcon
          />
        ) : null}

        {bulkMode ? (
          <section className="rtu-ping-modal__summary-rail" aria-label="Ping diagnostic summary">
            <div className="rtu-ping-modal__summary-progress">
              <Progress percent={Math.min(100, progressPercent)} status={running ? 'active' : 'normal'} showInfo={false} />
              <span className="rtu-ping-modal__summary-count">
                {completedRuns}/{Math.max(1, requestedRuns)}
              </span>
            </div>
            <div className="rtu-ping-modal__status-pills">
              <span className="rtu-ping-modal__status-pill is-online">Online {onlineCount}</span>
              <span className="rtu-ping-modal__status-pill is-unstable">Unstable {unstableCount}</span>
              <span className="rtu-ping-modal__status-pill is-offline">Offline {offlineCount}</span>
              <span className="rtu-ping-modal__status-pill is-invalid">Invalid Target {invalidCount}</span>
              <span className="rtu-ping-modal__status-pill is-running">Pinging {runningCount}</span>
              <span className="rtu-ping-modal__status-pill is-pending">Pending {pendingCount}</span>
            </div>
          </section>
        ) : null}

        <section className={`rtu-ping-modal__body-grid ${bulkMode ? '' : 'is-single'}`}>
          {bulkMode ? (
            <div className="rtu-ping-modal__list-panel">
              <div className="rtu-ping-modal__panel-title">Selected RTUs</div>
              <div className="rtu-ping-modal__rtu-list">
                {runs.length === 0 ? <div className="rtu-ping-modal__empty">No RTUs selected.</div> : null}
                {runs.map((runItem) => {
                  const visualStatus = getRunVisualStatus(runItem);
                  const statusMeta = statusDisplay[visualStatus];
                  const successCount = getSuccessCount(runItem);
                  const attemptCount = getAttemptCount(runItem);
                  const itemProgress = Math.round(((successCount + getFailureCount(runItem)) / Math.max(1, attemptCount)) * 100);
                  const runKey = getRunKey(runItem);
                  return (
                    <button
                      key={runKey}
                      type="button"
                      className={`rtu-ping-modal__rtu-row ${selectedRun && getRunKey(selectedRun) === runKey ? 'is-selected' : ''}`}
                      onClick={() => setSelectedRunKey(runKey)}
                      title={`${getRunName(runItem)} · ${runItem.targetIp || 'No target'} · ${getRunStatusLabel(runItem)}`}
                    >
                      <strong>{getRunName(runItem)}</strong>
                      <span>{runItem.targetIp || 'No target'}</span>
                      <span>
                        {successCount}/{attemptCount}
                      </span>
                      <Tag className={`rtu-ping-modal__tag ${statusMeta.className}`}>{getRunStatusLabel(runItem)}</Tag>
                      <span className="rtu-ping-modal__row-progress" aria-hidden="true">
                        <span className={statusMeta.className} style={{ width: `${Math.min(100, itemProgress)}%` }} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="rtu-ping-modal__detail-panel">
            {selectedRun ? (
              <>
                <div className="rtu-ping-modal__detail-header">
                  <div className="rtu-ping-modal__detail-identity">
                    <h3>
                      <strong>{getRunName(selectedRun)}</strong>
                      <span>{selectedRun.targetIp || 'No target'}</span>
                    </h3>
                  </div>
                  {renderAttemptProgress(selectedRun)}
                  <Tag className={`rtu-ping-modal__tag ${statusDisplay[getRunVisualStatus(selectedRun)].className}`}>{getRunStatusLabel(selectedRun)}</Tag>
                </div>

                <div className="rtu-ping-modal__detail-strip">
                  <span>
                    Success <strong>{getSuccessCount(selectedRun)}/{getAttemptCount(selectedRun)}</strong>
                  </span>
                  <span>
                    Loss <strong>{formatPacketLoss(selectedRun.packetLossPercent)}</strong>
                  </span>
                  <span>
                    Avg <strong>{formatResponseTime(selectedRun.avgResponseMs)}</strong>
                  </span>
                  <span className="rtu-ping-modal__last-attempt-card">
                    Last attempt <strong>{getLastAttemptTime(selectedRun)}</strong>
                  </span>
                </div>

                <div className="rtu-ping-modal__terminal">
                  <div className="rtu-ping-modal__terminal-title">
                    <span>Terminal output</span>
                  </div>
                  <pre className="rtu-ping-modal__raw-output">{buildRawOutput(selectedRun)}</pre>
                </div>
              </>
            ) : (
              <div className="rtu-ping-modal__empty">Select an RTU to inspect ping progress.</div>
            )}
          </div>
        </section>
      </Modal>
    </ConfigProvider>
  );
};

export default RtuPingDialog;
